import React, { useEffect, useRef } from 'react';

/* =================================================================
   SCROLL CAR v7

   Root cause fixes over v6:
   ─────────────────────────────────────────────────────────────────
   • Anchored via CSS `right` + `transformOrigin: top right`
     → car always hugs the right viewport edge, never clips out
   • Hero Y = viewH * 0.36 (alongside the hero text, not top)
   • Hero right = 8px  (car visually to the RIGHT of centered text)
   • Seed st.current from computeTargets(0) BEFORE first rAF tick
     → zero jump on page load
   • Footer-aware: clamps Y above footer.getBoundingClientRect().top

   ─── PHASES ──────────────────────────────────────────────────────
   p=0 … P_SHRINK_S   Hero — large, right side, text-level Y
   P_SHRINK_S…E       Shrinks, moves UP to track top
   P_SHRINK_E…P_SIDE_S Track — small, travels top→bottom
   P_SIDE_S…P_SIDE_E  Crossfade side-view, grows
   P_CENTER_S…1       Slides horizontally to page center
   ================================================================= */

const TD_W   = 52;
const TD_H   = 82;
const SV_W   = 160;
const SV_H   = 80;
const WRAP_W = 180;
const WRAP_H = 100;

const TRACK_RIGHT = 8;    // wrapper right edge from viewport right (px)
const HEADER_H    = 72;   // top of track (below header)

const S_HERO   = 1.75;
const S_TRACK  = 0.58;
const S_FINISH = 1.80;

const P_SHRINK_S = 0.06;
const P_SHRINK_E = 0.20;
const P_SIDE_S   = 0.90;
const P_SIDE_E   = 0.92;

const K_POS   = 0.07;
const K_SCALE = 0.06;
const K_ROT   = 0.09;
const K_SIDE  = 0.22;

const DOT_SPACING = 18;
const DOT_FADE    = 1.8;

function eio(t: number) {
  return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2;
}

function computeTargets(p: number, viewW: number, viewH: number) {
  /* rightPx — wrapper's right edge distance from viewport right */
  // Car stays flush-right at all times; no horizontal centering in hero
  // phase. The hero video is now the background element so the car just
  // travels in a clean vertical track along the right edge.
  const heroRight = TRACK_RIGHT;
  let tRight: number;
  if (p <= P_SHRINK_S) {
    tRight = heroRight;
  } else if (p <= P_SHRINK_E) {
    tRight = heroRight + eio((p - P_SHRINK_S) / (P_SHRINK_E - P_SHRINK_S)) * (TRACK_RIGHT - heroRight);
  } else if (p <= P_SIDE_S) {
    tRight = TRACK_RIGHT;
  } else {
    const t = Math.min(1, (p - P_SIDE_S) / (P_SIDE_E - P_SIDE_S));
    // When centered: car center = viewW/2, car right = viewW/2 + SV_W*S_FINISH/2
    // wrapper right from viewport = viewW - (viewW/2 + SV_W*S_FINISH/2) = viewW/2 - SV_W*S_FINISH/2
    const centerRight = Math.max(8, viewW / 2 - SV_W * S_FINISH / 2);
    tRight = TRACK_RIGHT + eio(t) * (centerRight - TRACK_RIGHT);
  }

  /* scale */
  let tScale: number;
  if (p <= P_SHRINK_S) {
    tScale = S_HERO;
  } else if (p <= P_SHRINK_E) {
    tScale = S_HERO + eio((p - P_SHRINK_S) / (P_SHRINK_E - P_SHRINK_S)) * (S_TRACK - S_HERO);
  } else if (p <= P_SIDE_S) {
    tScale = S_TRACK;
  } else {
    tScale = S_TRACK + eio(Math.min(1, (p - P_SIDE_S) / (P_SIDE_E - P_SIDE_S))) * (S_FINISH - S_TRACK);
  }

  /* Y */
  const heroY = viewH * 0.36;
  const botY  = viewH * 0.86 - WRAP_H * S_TRACK;
  let tY: number;
  if (p <= P_SHRINK_S) {
    tY = heroY;
  } else if (p <= P_SHRINK_E) {
    // move UP from heroY to HEADER_H while shrinking
    tY = heroY + eio((p - P_SHRINK_S) / (P_SHRINK_E - P_SHRINK_S)) * (HEADER_H - heroY);
  } else if (p <= P_SIDE_S) {
    const track = (p - P_SHRINK_E) / (P_SIDE_S - P_SHRINK_E);
    tY = HEADER_H + track * (botY - HEADER_H);
  } else {
    tY = botY;
  }

  /* side crossfade */
  const tSide = p > P_SIDE_S
    ? Math.min(1, (p - P_SIDE_S) / (P_SIDE_E - P_SIDE_S))
    : 0;

  return { tRight, tScale, tY, tSide };
}

const ScrollCar: React.FC = () => {
  const wrapRef   = useRef<HTMLDivElement>(null);
  const tdRef     = useRef<SVGSVGElement>(null);
  const svRef     = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const footerRef = useRef<Element | null>(null);

  const dots       = useRef<{ x: number; y: number; age: number }[]>([]);
  const lastDotPos = useRef<{ x: number; y: number } | null>(null);
  const hasScrolledOnce = useRef(false);
  const carOpacity      = useRef(0);

  const st = useRef({
    rightPx: TRACK_RIGHT,
    y:       200,
    scale:   S_HERO,
    rot:     0,
    side:    0,
    vel:     0,
    lastSY:  -1,
    cx:      0,
    cy:      0,
  });

  useEffect(() => {
    const wrap = wrapRef.current;
    const td   = tdRef.current;
    const sv   = svRef.current;
    if (!wrap || !td || !sv) return;

    footerRef.current = document.querySelector('footer');

    // Seed from targets(0) so there is zero jump on first frame
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const init = computeTargets(0, vw, vh);
    const c    = st.current;
    c.rightPx = init.tRight;
    c.y       = init.tY;
    c.scale   = init.tScale;
    c.lastSY  = window.scrollY;

    let raf: number;

    const tick = () => {
      const sy    = window.scrollY;
      const viewH = window.innerHeight;
      const viewW = window.innerWidth;
      const maxSY = Math.max(1, document.documentElement.scrollHeight - viewH);
      const p     = Math.min(1, Math.max(0, sy / maxSY));

      c.vel    = c.vel * 0.78 + (sy - c.lastSY) * 0.22;
      c.lastSY = sy;

      const { tRight, tScale, tY, tSide } = computeTargets(p, viewW, viewH);

      // Footer-aware Y ceiling
      let maxY = viewH - WRAP_H * c.scale - 4;
      const footer = footerRef.current ?? document.querySelector('footer');
      if (footer) {
        footerRef.current = footer;
        const fr = footer.getBoundingClientRect();
        if (fr.top < viewH) {
          maxY = Math.min(maxY, fr.top - WRAP_H * c.scale - 8);
        }
      }

      const clampedY     = Math.min(Math.max(HEADER_H, tY), Math.max(HEADER_H, maxY));
      const clampedScale = Math.min(tScale, Math.max(0.4, (viewH - clampedY - 4) / WRAP_H));
      const tRot         = Math.max(-3.5, Math.min(3.5, c.vel * 0.3));

      c.rightPx += (tRight        - c.rightPx) * K_POS;
      c.y       += (clampedY      - c.y)       * K_POS;
      c.scale   += (clampedScale  - c.scale)   * K_SCALE;
      c.rot     += (tRot          - c.rot)     * K_ROT;
      c.side    += (tSide         - c.side)    * K_SIDE;

      c.y = Math.max(HEADER_H, Math.min(c.y, maxY));

      // ── Part C: fade in on first scroll ──
      if (sy > 0) hasScrolledOnce.current = true;
      const targetOp = hasScrolledOnce.current ? 1 : 0;
      carOpacity.current += (targetOp - carOpacity.current) * 0.08;

      // Apply — `right` + `transformOrigin: top right` means scale never clips right side
      wrap.style.right     = `${c.rightPx.toFixed(1)}px`;
      wrap.style.opacity   = carOpacity.current.toFixed(3);
      wrap.style.left      = 'auto';
      wrap.style.transform = `translateY(${c.y.toFixed(1)}px) scale(${c.scale.toFixed(4)})`;

      // Sequential flip: first half collapses top-down (scaleX 1→0), second half expands side (scaleX 0→1).
      // Both cars are NEVER simultaneously visible — no ghosting/blending.
      const flip = c.side; // 0→1, lerped
      if (flip < 0.5) {
        const sx = 1 - eio(flip * 2); // ease 1→0
        td.style.opacity   = '1';
        td.style.transform = `rotate(${c.rot.toFixed(2)}deg) scaleX(${sx.toFixed(3)})`;
        sv.style.opacity   = '0';
        sv.style.transform = 'scaleX(0)';
      } else {
        const sx = eio((flip - 0.5) * 2); // ease 0→1
        td.style.opacity   = '0';
        td.style.transform = `rotate(${c.rot.toFixed(2)}deg) scaleX(0)`;
        sv.style.opacity   = '1';
        sv.style.transform = `scaleX(${sx.toFixed(3)})`;
      }

      // Dot trail emission point
      const isSide = c.side > 0.5;
      if (isSide) {
        c.cx = viewW - c.rightPx - SV_W * c.scale * 0.72;
        c.cy = c.y + WRAP_H * c.scale;
      } else {
        // top-down: bottom-center of car body
        // car is right-aligned in wrapper → car right = wrapper right
        // car center x = viewW - c.rightPx - TD_W*c.scale/2
        c.cx = viewW - c.rightPx - TD_W * c.scale / 2;
        c.cy = c.y + WRAP_H * c.scale;
      }

      const last = lastDotPos.current;
      if (hasScrolledOnce.current && (!last || Math.hypot(c.cx - last.x, c.cy - last.y) >= DOT_SPACING)) {
        dots.current.push({ x: c.cx, y: c.cy, age: 0 });
        lastDotPos.current = { x: c.cx, y: c.cy };
        if (dots.current.length > 200) dots.current.shift();
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf: number;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const pts = dots.current;
      for (const pt of pts) pt.age += 0.016;
      while (pts.length && pts[0].age > DOT_FADE) pts.shift();

      for (const pt of pts) {
        const life  = Math.max(0, 1 - pt.age / DOT_FADE);
        if (life < 0.03) continue;
        const alpha = life * life * carOpacity.current;
        const r     = 2.5 * life + 0.8;
        for (const off of [-5, 5]) {
          ctx.beginPath();
          ctx.arc(pt.x + off, pt.y, r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(94,234,212,${(alpha * 0.38).toFixed(3)})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(pt.x + off, pt.y, r * 0.42, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${(alpha * 0.55).toFixed(3)})`;
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);

  const tdTop = (WRAP_H - TD_H) / 2;
  const svTop = (WRAP_H - SV_H) / 2;

  return (
    <>
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 5 }} />

      <div
        ref={wrapRef}
        className="pointer-events-none"
        style={{
          position:        'fixed',
          right:           TRACK_RIGHT,
          top:             0,
          width:           WRAP_W,
          height:          WRAP_H,
          zIndex:          6,
          opacity:         0,
          transformOrigin: 'top right',
          willChange:      'transform, right, opacity',
        }}
      >
        {/* ═══ TOP-DOWN VIEW ═══ */}
        <svg
          ref={tdRef}
          viewBox="0 0 44 70"
          style={{
            position:        'absolute',
            right:           0,
            top:             tdTop,
            width:           TD_W,
            height:          TD_H,
            overflow:        'visible',
            transformOrigin: 'center center',
          }}
        >
          <ellipse cx="22" cy="68" rx="13" ry="2.5" fill="rgba(0,0,0,0.12)" />
          <path d="M10 8 Q10 2 22 2 Q34 2 34 8 L35 58 Q35 65 22 65 Q9 65 9 58 Z" fill="#1E293B"/>
          <path d="M14 8 L14 20" stroke="#334155" strokeWidth="0.6" opacity="0.7"/>
          <path d="M30 8 L30 20" stroke="#334155" strokeWidth="0.6" opacity="0.7"/>
          <path d="M14 20 Q22 22.5 30 20" stroke="#334155" strokeWidth="0.5" fill="none" opacity="0.6"/>
          <path d="M14 21 Q14 15 22 15 Q30 15 30 21 L29 29 Q22 31 15 29 Z" fill="#5EEAD4" opacity="0.55"/>
          <path d="M16 22 Q20 18 25 21" stroke="rgba(255,255,255,0.6)" strokeWidth="1.1" fill="none" strokeLinecap="round"/>
          <path d="M14 29 L15 44 Q22 46 29 44 L30 29 Z" fill="#263548"/>
          <rect x="20" y="31" width="4" height="10" rx="1" fill="#1e293b" opacity="0.6"/>
          <path d="M15 44 Q15 50 22 50 Q29 50 29 44 Z" fill="#C4B5FD" opacity="0.40"/>
          <path d="M12 50 L12 58 Q12 63 22 63 Q32 63 32 58 L32 50 Z" fill="#1a2d40"/>
          <path d="M14 51 Q22 53 30 51" stroke="#334155" strokeWidth="0.5" fill="none" opacity="0.5"/>
          <line x1="12" y1="36" x2="32" y2="36" stroke="#334155" strokeWidth="0.7" opacity="0.55"/>
          <line x1="12" y1="29" x2="12" y2="44" stroke="#334155" strokeWidth="0.5" opacity="0.4"/>
          <line x1="32" y1="29" x2="32" y2="44" stroke="#334155" strokeWidth="0.5" opacity="0.4"/>
          <rect x="9.5" y="33.5" width="3" height="1.5" rx="0.7" fill="#475569"/>
          <rect x="31.5" y="33.5" width="3" height="1.5" rx="0.7" fill="#475569"/>
          <path d="M10 4 Q8 4 8 7 L8 12 Q8 14 10 14 L13 14 L13 4 Z" fill="#FDE68A" opacity="0.88"/>
          <path d="M34 4 Q36 4 36 7 L36 12 Q36 14 34 14 L31 14 L31 4 Z" fill="#FDE68A" opacity="0.88"/>
          <ellipse cx="9"  cy="9" rx="2.2" ry="4.5" fill="#FDE68A" opacity="0.25"/>
          <ellipse cx="35" cy="9" rx="2.2" ry="4.5" fill="#FDE68A" opacity="0.25"/>
          <path d="M10 61 Q9 62 9 64 L12 64 L12 60 Z" fill="#FDA4AF" opacity="0.92"/>
          <path d="M34 61 Q35 62 35 64 L32 64 L32 60 Z" fill="#FDA4AF" opacity="0.92"/>
          {/* Wheels */}
          <rect x="4"  y="9"  width="8" height="13" rx="3.5" fill="#0F172A"/>
          <rect x="32" y="9"  width="8" height="13" rx="3.5" fill="#0F172A"/>
          <circle cx="8"  cy="15.5" r="3.0" fill="#1e293b" stroke="#475569" strokeWidth="0.7"/>
          <circle cx="36" cy="15.5" r="3.0" fill="#1e293b" stroke="#475569" strokeWidth="0.7"/>
          <circle cx="8"  cy="15.5" r="1.1" fill="#64748b"/>
          <circle cx="36" cy="15.5" r="1.1" fill="#64748b"/>
          {[0,1,2,3,4].map(i => {
            const a = (i/5)*Math.PI*2;
            return <g key={i}>
              <line x1={8 +Math.cos(a)*1.2} y1={15.5+Math.sin(a)*1.2} x2={8 +Math.cos(a)*2.6} y2={15.5+Math.sin(a)*2.6} stroke="#475569" strokeWidth="0.6"/>
              <line x1={36+Math.cos(a)*1.2} y1={15.5+Math.sin(a)*1.2} x2={36+Math.cos(a)*2.6} y2={15.5+Math.sin(a)*2.6} stroke="#475569" strokeWidth="0.6"/>
            </g>;
          })}
          <rect x="4"  y="48" width="8" height="13" rx="3.5" fill="#0F172A"/>
          <rect x="32" y="48" width="8" height="13" rx="3.5" fill="#0F172A"/>
          <circle cx="8"  cy="54.5" r="3.0" fill="#1e293b" stroke="#475569" strokeWidth="0.7"/>
          <circle cx="36" cy="54.5" r="3.0" fill="#1e293b" stroke="#475569" strokeWidth="0.7"/>
          <circle cx="8"  cy="54.5" r="1.1" fill="#64748b"/>
          <circle cx="36" cy="54.5" r="1.1" fill="#64748b"/>
          {[0,1,2,3,4].map(i => {
            const a = (i/5)*Math.PI*2;
            return <g key={i}>
              <line x1={8 +Math.cos(a)*1.2} y1={54.5+Math.sin(a)*1.2} x2={8 +Math.cos(a)*2.6} y2={54.5+Math.sin(a)*2.6} stroke="#475569" strokeWidth="0.6"/>
              <line x1={36+Math.cos(a)*1.2} y1={54.5+Math.sin(a)*1.2} x2={36+Math.cos(a)*2.6} y2={54.5+Math.sin(a)*2.6} stroke="#475569" strokeWidth="0.6"/>
            </g>;
          })}
          <rect x="18" y="31" width="8" height="3.5" rx="1" fill="#5EEAD4" opacity="0.25"/>
        </svg>

        {/* ═══ SIDE VIEW ═══ */}
        <svg
          ref={svRef}
          viewBox="0 0 140 70"
          style={{
            position:        'absolute',
            right:           0,
            top:             svTop,
            width:           SV_W,
            height:          SV_H,
            overflow:        'visible',
            opacity:         0,
            transformOrigin: 'center center',
          }}
        >
          <defs>
            <linearGradient id="svBody" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#334155"/>
              <stop offset="100%" stopColor="#1E293B"/>
            </linearGradient>
            <linearGradient id="svGlass" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%"   stopColor="#5EEAD4" stopOpacity="0.72"/>
              <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0.52"/>
            </linearGradient>
          </defs>
          <ellipse cx="70" cy="67.5" rx="58" ry="3.5" fill="rgba(0,0,0,0.14)"/>
          <rect x="8" y="40" width="124" height="18" rx="4" fill="url(#svBody)"/>
          <path d="M22 40 Q24 18 38 16 L58 13 Q72 11 84 13 L102 16 Q116 18 118 40 Z" fill="url(#svBody)"/>
          <path d="M40 16 L40 13 Q56 7 84 7 L100 13 L100 16 Z" fill="#263548"/>
          <path d="M118 40 Q130 40 133 44 L133 51 Q130 57 118 58 Z" fill="#1E293B"/>
          <path d="M8 40 Q3 42 3 46 L4 52 Q6 58 14 58 L8 58 Z" fill="#1E293B"/>
          <path d="M100 13 L100 40 L84 40 L84 13 Z" fill="url(#svGlass)" opacity="0.82"/>
          <path d="M86 17 L89 14" stroke="rgba(255,255,255,0.65)" strokeWidth="1.3" strokeLinecap="round"/>
          <path d="M62 13 L62 40 L84 40 L84 13 Z" fill="url(#svGlass)" opacity="0.68"/>
          <path d="M40 16 L40 40 L62 40 L62 13 Z" fill="url(#svGlass)" opacity="0.58"/>
          <path d="M38 16 L38 40 L40 40 L40 16 Z" fill="url(#svGlass)" opacity="0.52"/>
          <line x1="62"  y1="13" x2="62"  y2="40" stroke="#1E293B" strokeWidth="2.8"/>
          <line x1="84"  y1="13" x2="84"  y2="40" stroke="#1E293B" strokeWidth="2.8"/>
          <line x1="100" y1="13" x2="100" y2="40" stroke="#1E293B" strokeWidth="2.2"/>
          <line x1="40"  y1="16" x2="40"  y2="40" stroke="#1E293B" strokeWidth="2.2"/>
          <line x1="10" y1="40" x2="120" y2="40" stroke="#0F172A" strokeWidth="1.5"/>
          <path d="M16 50 Q70 48 118 50" stroke="#263548" strokeWidth="1.1" fill="none" opacity="0.75"/>
          <rect x="54" y="45" width="11" height="3" rx="1.5" fill="#475569"/>
          <rect x="87" y="45" width="11" height="3" rx="1.5" fill="#475569"/>
          <path d="M118 40 L130 41 Q134 44 130 51 L118 51 Z" fill="#111827"/>
          <path d="M120 42 Q127 43 130 47 L120 49 Z" fill="#FDE68A" opacity="0.88"/>
          <ellipse cx="127" cy="45.5" rx="4.5" ry="3.2" fill="#FDE68A" opacity="0.22"/>
          <path d="M119 42 Q128 41 131 44" stroke="#FDE68A" strokeWidth="1.1" fill="none" opacity="0.75"/>
          <rect x="4"   y="41" width="10" height="15" rx="2" fill="#111827"/>
          <rect x="5.5" y="43" width="7"  height="11" rx="1.5" fill="#FDA4AF" opacity="0.85"/>
          <ellipse cx="9" cy="48.5" rx="5.5" ry="7" fill="#FDA4AF" opacity="0.15"/>
          {/* Wheels */}
          {[110, 32].map(cx => (
            <g key={cx}>
              <circle cx={cx} cy="58" r="12.5" fill="#0F172A"/>
              <circle cx={cx} cy="58" r="9"    fill="#1E293B"/>
              <circle cx={cx} cy="58" r="3.8"  fill="#334155"/>
              <circle cx={cx} cy="58" r="1.8"  fill="#64748B"/>
              <circle cx={cx} cy="58" r="12.5" fill="none" stroke="#334155" strokeWidth="1"/>
              {[0,1,2,3,4].map(i => {
                const a = (i/5)*Math.PI*2;
                return <line key={i}
                  x1={cx+Math.cos(a)*4} y1={58+Math.sin(a)*4}
                  x2={cx+Math.cos(a)*8} y2={58+Math.sin(a)*8}
                  stroke="#475569" strokeWidth="2" strokeLinecap="round"/>;
              })}
            </g>
          ))}
          <path d="M20 58 Q70 56.5 120 58" stroke="#5EEAD4" strokeWidth="1.6" fill="none" opacity="0.5"/>
        </svg>
      </div>
    </>
  );
};

export default ScrollCar;
